import { Trash2 } from 'lucide-react';
import { GroupResponse } from '../../lib/groups';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';

interface GroupsListProps {
  groups: GroupResponse[];
  onEdit: (group: GroupResponse) => void;
  onDelete: (id: string) => void;
  isDeleting: boolean;
}

export default function GroupsList({ groups, onEdit, onDelete, isDeleting }: GroupsListProps) {
  const handleRowClick = (group: GroupResponse) => {
    onEdit(group);
  };
  
  if (groups.length === 0) {
    return (
      <Card className="text-center py-12">
        <CardContent>
          <p className="text-muted-foreground">No groups available</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <div className="overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="px-3 sm:px-4 md:px-6">Name</TableHead>
              <TableHead className="hidden sm:table-cell px-3 sm:px-4 md:px-6">PIN</TableHead>
              <TableHead className="px-3 sm:px-4 md:px-6">Members</TableHead>
              <TableHead className="hidden lg:table-cell px-3 sm:px-4 md:px-6">Controller Preset</TableHead>
              <TableHead className="hidden lg:table-cell px-3 sm:px-4 md:px-6">Created</TableHead>
              <TableHead className="text-right px-3 sm:px-4 md:px-6">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {groups.map((group) => (
              <TableRow 
                key={group.id} 
                className="cursor-pointer hover:bg-muted/50"
                onClick={() => handleRowClick(group)}
              >
                <TableCell className="px-3 sm:px-4 md:px-6 max-w-0">
                  <div className="truncate" title={group.name}>
                    {group.name}
                  </div>
                </TableCell>
                <TableCell className="hidden sm:table-cell px-3 sm:px-4 md:px-6 whitespace-nowrap">
                  {group.pin}
                </TableCell>
                <TableCell className="px-3 sm:px-4 md:px-6">
                  {group.members && group.members.length > 0 ? (
                    <Badge variant="secondary">
                      {group.members.length} {group.members.length === 1 ? 'member' : 'members'}
                    </Badge>
                  ) : (
                    'No members'
                  )}
                </TableCell>
                <TableCell className="hidden lg:table-cell px-3 sm:px-4 md:px-6">
                  {group.controllerUserPresetName ? (
                    <div className="truncate" title={group.controllerUserPresetName}>
                      {group.controllerUserPresetName}
                    </div>
                  ) : (
                    <span className="text-muted-foreground">—</span>
                  )}
                </TableCell>
                <TableCell className="hidden lg:table-cell px-3 sm:px-4 md:px-6 whitespace-nowrap">
                  {new Date(group.createdAt).toLocaleDateString()}
                </TableCell>
                <TableCell className="px-3 sm:px-4 md:px-6 whitespace-nowrap text-right">
                  <div className="flex justify-end gap-2 sm:gap-3 flex-shrink-0" onClick={(e) => e.stopPropagation()}>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => onDelete(group.id)}
                      disabled={isDeleting}
                      className="text-destructive hover:text-destructive"
                      title="Delete group"
                    >
                      <Trash2 className="h-4 w-4" />
                      <span className="hidden sm:inline ml-1">Delete</span>
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </Card>
  );
}
